import React, { createContext, useContext, useEffect, useState } from 'react';
import { API, graphqlOperation, Auth } from 'aws-amplify';
import { format } from 'date-fns';
import { useSubscription } from './SubscriptionContext';

interface ViewLimitContextType {
    remainingViews: number;
    maxViews: number;
    isLoading: boolean;
    error: string | null;
    incrementViewCount: () => Promise<boolean>;
    showLimitPopup: boolean;
    closeLimitPopup: () => void;
}

const ViewLimitContext = createContext<ViewLimitContextType | undefined>(undefined);

export const ViewLimitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [remainingViews, setRemainingViews] = useState<number>(0);
    const [views, setViews] = useState<number>(5);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [showLimitPopup, setShowLimitPopup] = useState(false);
    const { subscriptionTier, isNewUserBonusActive, isLoading: isSubscriptionLoading } = useSubscription();

    useEffect(() => {
        if (isSubscriptionLoading) {
            setIsLoading(true); 
            return; 
        }
        fetchUserAndLimits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptionTier, isNewUserBonusActive, isSubscriptionLoading]);

    const fetchUserAndLimits = async () => {
        setIsLoading(true); 

        if (isNewUserBonusActive) {
            console.log('[ViewLimit] New user bonus active. Granting unlimited views.');
            setViews(Infinity);
            setRemainingViews(Infinity);
            setShowLimitPopup(false);
            setError(null);
            setIsLoading(false);
            return;
        }

        try {
            const { attributes } = await Auth.currentAuthenticatedUser();
            setUserId(attributes.sub);

            const fetchUserQuery = `
                query GetUser($id: ID!) {
                    getUser(id: $id) {
                        views
                        stripeStatus 
                    }
                }
            `;

            const userResponse: any = await API.graphql(graphqlOperation(fetchUserQuery, { id: attributes.sub }));
            const userData = userResponse.data?.getUser;
            const currentViews = userData?.views || 0;
            const stripeStatus = userData?.stripeStatus; 
            const maxViewsForFreeTier = 5;

            console.log('[ViewLimit] fetchUserAndLimits - User Data:', { userId: attributes.sub, subscriptionTier, stripeStatus, currentViews, isNewUserBonusActive });

            if (stripeStatus === "light" || stripeStatus === "premium") {
                console.log('[ViewLimit] Active subscription found (light/premium via stripeStatus). Granting unlimited views.');
                setViews(Infinity);
                setRemainingViews(Infinity);
                setShowLimitPopup(false);
            } else if (subscriptionTier !== "free") {
                console.log('[ViewLimit] Active subscription found (non-free via subscriptionTier). Granting unlimited views.');
                setViews(Infinity);
                setRemainingViews(Infinity);
                setShowLimitPopup(false);
            } else {
                console.log('[ViewLimit] Free tier or no active paid stripeStatus. Calculating remaining views.');
                setViews(maxViewsForFreeTier);
                const newRemainingViews = Math.max(0, maxViewsForFreeTier - currentViews);
                setRemainingViews(newRemainingViews);
                setShowLimitPopup(currentViews >= maxViewsForFreeTier);
            }

            setError(null);
        } catch (error: any) {
            console.error('Error fetching view limits:', error);
            setError(error.message);
            setViews(5);
            setRemainingViews(5);
            setShowLimitPopup(false); 
        } finally {
            setIsLoading(false);
        }
    };

    const incrementViewCount = async (): Promise<boolean> => {
        // First, check if the subscription context is still loading.
        // If so, limits (including bonus) are not yet determined.
        if (isSubscriptionLoading) {
            console.warn('[ViewLimit] incrementViewCount called while subscription context is loading. View denied temporarily.');
            // Do not set main isLoading state here, as it's likely already true due to isSubscriptionLoading in useEffect.
            // Optionally, set a specific error for the user if desired, though simply denying the view might be enough.
            // setError("View limits are still loading. Please try again in a moment.");
            return false; // Deny view as limits are uncertain.
        }

        setIsLoading(true); // ViewLimitContext's own loading for this specific operation

        if (isNewUserBonusActive) {
            console.log('[ViewLimit] incrementViewCount - New user bonus active. View allowed.');
            setIsLoading(false);
            return true; 
        }

        let userStripeStatus = null;
        let currentSubscriptionTier = subscriptionTier; 

        if (userId) {
            try {
                const gqlResponse: any = await API.graphql(graphqlOperation(
                    `query GetUser($id: ID!) { getUser(id: $id) { stripeStatus } }`,
                    { id: userId }
                ));
                userStripeStatus = gqlResponse.data?.getUser?.stripeStatus;
                console.log('[ViewLimit] incrementViewCount - Fetched stripeStatus:', userStripeStatus);
            } catch (e) {
                console.error("[ViewLimit] incrementViewCount - Error fetching stripeStatus:", e);
            }
        }

        if (userStripeStatus === "light" || userStripeStatus === "premium") {
            console.log('[ViewLimit] incrementViewCount - Active paid subscription (stripeStatus). View allowed.');
            setIsLoading(false);
            return true; 
        }
        if (currentSubscriptionTier !== "free") {
            console.log('[ViewLimit] incrementViewCount - Active non-free subscription (subscriptionTier). View allowed.');
            setIsLoading(false);
            return true; 
        }

        console.log('[ViewLimit] incrementViewCount - Free tier logic applying.');
        if (!userId || remainingViews < 1) {
            setRemainingViews(0);
            setShowLimitPopup(true);
            console.log('[ViewLimit] incrementViewCount - Limit reached on free tier.');
            setIsLoading(false);
            return false;
        }

        try {
            const fetchUserQuery = `
                query GetUserViews($id: ID!) {
                    getUser(id: $id) {
                        views
                    }
                }
            `;
            const userResponse: any = await API.graphql(graphqlOperation(fetchUserQuery, { id: userId }));
            const currentDBViews = userResponse.data?.getUser?.views || 0;
            const maxViewsForFreeTier = 5;
            const newViews = currentDBViews + 1;

            const updateUserMutation = `
                mutation UpdateUserViews($input: UpdateUserInput!) {
                    updateUser(input: $input) {
                        id
                        views
                    }
                }
            `;
            const updateResponse: any = await API.graphql(graphqlOperation(updateUserMutation, { input: { id: userId, views: newViews } }));
            const updatedViews = updateResponse.data?.updateUser?.views ?? newViews;
            const newRemainingViews = Math.max(0, maxViewsForFreeTier - updatedViews);

            setRemainingViews(newRemainingViews);
            setShowLimitPopup(newRemainingViews === 0);
            console.log('[ViewLimit] incrementViewCount - Free tier view recorded:', {
                userId,
                updatedViews,
                newRemainingViews,
                showLimitPopup: newRemainingViews === 0
            });
            setIsLoading(false);
            return newRemainingViews >= 0; 
        } catch (error: any) {
            setError(error.message || 'Failed to increment view count');
            console.error('[ViewLimit] incrementViewCount - Error updating views:', error);
            setIsLoading(false);
            return false; 
        }
    };

    const closeLimitPopup = () => setShowLimitPopup(false);

    return (
        <ViewLimitContext.Provider
            value={{
                remainingViews,
                maxViews: views,
                isLoading,
                error,
                incrementViewCount,
                showLimitPopup,
                closeLimitPopup
            }}
        >
            {children}
        </ViewLimitContext.Provider>
    );
};

export const useViewLimit = () => {
    const context = useContext(ViewLimitContext);
    if (!context) {
        throw new Error('useViewLimit must be used within a ViewLimitProvider');
    }
    return context;
};
